(function (scope, factory) {
  'use strict';
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else if (scope?.document) api.mount(scope);
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';
  function integrated(selection) {
    if (selection?.offer === 'single') return selection.letter1 === 'A' && selection.tone1 === 'Rosado' && (!selection.theme1 || selection.theme1 === 'Sin Temática');
    return ['pareja-xl', 'pareja'].includes(selection?.offer) && selection.letter1 === 'A' && selection.letter2 === 'C' && selection.tone1 === 'Rosado' && selection.tone2 === 'Neutro' && (!selection.theme1 || selection.theme1 === 'Sin Temática') && (!selection.theme2 || selection.theme2 === 'Sin Temática');
  }
  function preservedSlots(selection) {
    // A rectangular scene patch also contains the table and neighbouring flowers.
    // Custom compositions use only isolated alpha cutouts over the clean scene.
    return { one: false, two: false, amp: false };
  }

  function plateRectangle(scene, slot) {
    const point = scene?.[slot];
    if (!point || !(scene.width > 0) || !(scene.height > 0)) return null;
    const height = point.height || ((scene.one.height + scene.two.height) / 2 * 10 / 19);
    const half = height * (slot === 'amp' ? .62 : .57);
    const x = Math.max(0, point.x - half), y = Math.max(0, point.base - height * 1.09);
    return { x: x / scene.width * 100, y: y / scene.height * 100,
      width: Math.min(scene.width - x, half * 2) / scene.width * 100,
      height: Math.min(scene.height - y, height * 1.24) / scene.height * 100 };
  }
  function mount(win) {
    const doc = win.document, instances = new Map();
    const Controller = win.AbortController || AbortController, lifecycle = new Controller();
    let disposed = false;
    function on(target, name, callback, controller = lifecycle, options = {}) {
      target?.addEventListener(name, callback, { ...options, signal: controller.signal });
    }
    function init(scope) {
      if (disposed) return;
      const roots = scope.matches?.('[data-fas-garden]') ? [scope] : scope.querySelectorAll('[data-fas-garden]');
      roots.forEach(root => {
        if (instances.has(root)) return;
        const form = root.querySelector('[data-fas-form]'), status = root.querySelector('[data-fg-preview-status]');
        if (!form || !status) return;
        const abort = new Controller(), records = new Map();
        const empty = Array.from(root.querySelectorAll('[data-fg-empty-photo]'));
        const full = Array.from(root.querySelectorAll('.fg-integrated-photo'));
        const small = Array.from(root.querySelectorAll('[data-fg-small-photo]'));
        const singleLarge = Array.from(root.querySelectorAll('.fg-single-large-photo'));
        const singleSmall = Array.from(root.querySelectorAll('.fg-single-small-photo'));
        let dead = false, previousSelection = '', previousScene = null, frame = null;
        const orientation = win.matchMedia ? win.matchMedia('(max-aspect-ratio: 1/1)') : null;
        function mark(record, state) {
          record.state = state;
          for (const key of ['ready', 'loading', 'failed']) {
            if (key === state) record.image.dataset[key] = 'true';
            else delete record.image.dataset[key];
          }
        }
        function track(image) {
          if (records.has(image)) return;
          const src = image.currentSrc || image.getAttribute('src') || image.src || '';
          const picture = image.closest?.('picture');
          const responsive = Array.from(picture?.querySelectorAll('source') || []);
          const record = { image, responsive, url: image.dataset.src || src, state: 'idle' };
          records.set(image, record);
          // Cached images may have completed before this deferred script runs.
          mark(record, image.complete && image.naturalWidth > 0 ? 'ready' : src ? (image.complete ? 'failed' : 'loading') : 'idle');
          on(image, 'load', () => {
            if (dead || disposed) return;
            mark(record, image.naturalWidth > 0 ? 'ready' : 'failed');
            update('resource');
          }, abort);
          on(image, 'error', () => {
            if (dead || disposed) return;
            if (!record.responsive.length && image.currentSrc) record.url = image.currentSrc;
            mark(record, 'failed');
            update('resource');
          }, abort);
        }
        empty.concat(full, small, singleLarge, singleSmall).forEach(track);
        function source(record, retry) {
          if (!record || record.state === 'ready' || record.state === 'loading' || (record.state === 'failed' && !retry)) return;
          if (!record.url) { mark(record, 'failed'); return; }
          const wasFailed = record.state === 'failed';
          mark(record, 'loading');
          try {
            // A failed responsive candidate must not keep overriding the retry URL.
            // Reuse the candidate already selected by the browser; no cache buster.
            if (wasFailed && record.image.getAttribute('srcset')) record.image.removeAttribute('srcset');
            record.responsive.forEach(candidate => {
              if (candidate.dataset.srcset && !candidate.getAttribute('srcset')) candidate.setAttribute('srcset', candidate.dataset.srcset);
            });
            record.image.src = record.url;
          } catch (_) { mark(record, 'failed'); }
        }
        function resized() {
          if (dead || disposed || frame !== null) return;
          frame = win.requestAnimationFrame(() => {
            frame = null;
            for (const record of records.values()) {
              if (record.state === 'idle' || !record.responsive.length) continue;
              mark(record, record.image.complete ? (record.image.naturalWidth > 0 ? 'ready' : 'failed') : 'loading');
            }
            update('orientation');
          });
        }
        function update(reason) {
          if (dead || disposed) return;
          const selection = Object.fromEntries(new win.FormData(form));
          const signature = JSON.stringify(selection), scene = Math.max(0, Math.min(3, Math.floor(Number(root.dataset.scene) || 0)));
          const changed = signature !== previousSelection || scene !== previousScene;
          // Image callbacks only report outcomes. Repeated commerce notifications
          // for the same selection cannot turn a failed request into a retry loop.
          const retry = reason === 'online' || (changed && (reason === 'selection' || reason === 'scene'));
          previousSelection = signature; previousScene = scene;
          root.querySelectorAll('[data-fg-choose-offer]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.fgChooseOffer === selection.offer)));
          const wantsFull = integrated(selection), wantsSmall = selection.offer === 'pareja';
          const isSingle = selection.offer === 'single', singleSize = selection.size === 'small' ? 'small' : 'large';
          const collection = isSingle ? (singleSize === 'small' ? singleSmall : singleLarge) : (wantsSmall ? small : full);
          const fullRecord = records.get(collection[scene]), emptyRecord = records.get(empty[scene]);
          root.dataset.fgPhotoSize = isSingle ? 'single-' + singleSize : (wantsSmall ? 'small' : 'large');
          const backdropImage = (wantsFull ? fullRecord : emptyRecord)?.image;
          const backdropURL = backdropImage?.currentSrc || records.get(backdropImage)?.url;
          if (backdropURL) {
            const room = backdropImage.closest?.('[data-fg-room]');
            if (room?.style) room.style.setProperty('--fg-editor-backdrop', 'url(' + JSON.stringify(backdropURL) + ')');
          }
          for (const slot of ['One', 'Two', 'Amp']) root.dataset['fgPreserved' + slot] = 'false';
          if (wantsFull) source(fullRecord, retry);
          if (wantsFull && fullRecord?.state === 'ready') {
            root.dataset.fgPhotoMode = 'integrated'; status.hidden = true; return;
          }
          if (!wantsFull || !fullRecord || fullRecord.state === 'failed') source(emptyRecord, retry);
          if (emptyRecord?.state === 'ready') {
            root.dataset.fgPhotoMode = 'custom';
            status.hidden = !wantsFull;
            if (wantsFull) status.textContent = fullRecord?.state === 'loading'
              ? 'La fotografía completa está cargando. Mostramos vuestra composición personalizada.'
              : 'No se pudo cargar la fotografía completa. Mostramos vuestra composición personalizada.';
            return;
          }
          root.dataset.fgPhotoMode = 'loading'; status.hidden = false;
          status.textContent = emptyRecord?.state === 'failed'
            ? 'No se pudo cargar este ambiente. Cambia de ambiente para reintentar o consulta las fotos reales.'
            : 'Preparando vuestro rincón…';
        }
        on(root, 'click', event => {
          const button = event.target.closest('[data-fg-choose-offer]');
          if (!button || !root.contains(button)) return;
          const offer = button.dataset.fgChooseOffer;
          if (!['pareja-xl', 'pareja', 'single'].includes(offer)) return;
          const input = form.querySelector('input[name="offer"][value="' + offer + '"]');
          if (input) { input.checked = true; form.dispatchEvent(new win.Event('change', { bubbles: true })); }
        }, abort);
        on(form, 'change', () => update('selection'), abort);
        on(form, 'input', () => update('selection'), abort);
        on(root, 'fas:selection', () => update('selection'), abort);
        if (orientation?.addEventListener) on(orientation, 'change', resized, abort);
        const observer = new win.MutationObserver(() => update('scene'));
        observer.observe(root, { attributes: true, attributeFilter: ['data-scene', 'data-fg-plate-safe'] });
        instances.set(root, {
          update,
          stop() { dead = true; abort.abort(); observer.disconnect(); if (frame !== null) win.cancelAnimationFrame(frame); frame = null; records.clear(); instances.delete(root); }
        });
        update('initial');
      });
    }
    function cleanup(scope) {
      instances.forEach((instance, root) => { if (scope === root || scope.contains(root)) instance.stop(); });
    }
    on(doc, 'shopify:section:load', event => init(event.target));
    on(doc, 'shopify:section:unload', event => cleanup(event.target));
    on(win, 'online', () => instances.forEach(instance => instance.update('online')));
    if (doc.readyState === 'loading') on(doc, 'DOMContentLoaded', () => init(doc), lifecycle, { once: true });
    else init(doc);
    return { dispose() { if (disposed) return; disposed = true; lifecycle.abort(); cleanup(doc); } };
  }
  return { integrated, preservedSlots, plateRectangle, mount };
});
